import {isObjectVisible} from '@Utils/fabric.util';
import {ElementDataType} from '@Libraries/add-media-library';
import {uploadFile, UserMediaType} from '@Libraries/user-media-library';
import {hideLoading, isLoaderActiveForKey, showLoading} from '@Libraries/loading-toast-library';
import type {DrawObject} from '@PosterWhiteboard/models/draw.class';
import {CIRCLE_BRUSH, DEFAULT_DRAW_COLOR, Draw, ERASER_BRUSH, PENCIL_BRUSH, SPRAY_BRUSH} from '@PosterWhiteboard/models/draw.class';
import type {RGBA} from '@Utils/color.util';
import {rgbaToRgbaString} from '@Utils/color.util';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {getUserId} from '@Libraries/user.library';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {ImageItemSource} from '@Libraries/image-item.library';
import type {CanvasEvents, FabricObject} from '@postermywall/fabricjs-2';
import {Group, PencilBrush, Canvas, CircleBrush, SprayBrush} from '@postermywall/fabricjs-2';
import type {ErasingEvent} from '@ThirdParty/eraser-brush/EraserBrush';
import {EraserBrush} from '@ThirdParty/eraser-brush/EraserBrush';
import {getScaleToFit} from '@Utils/math.util';
import {getUniqueString} from '@Utils/string.util';
import type {DeepPartial} from '@/global';
import {CommonMethods} from '@PosterWhiteboard/common-methods';

const DRAWING_LOADING_KEY = 'loadingDrawing';
const HIGH_RES_DRAWING_MAX_DIMENSION = 6000;
const DRAWING_IMAGE_EXT = 'webp';

interface DrawingEvent {
  path: FabricObject;
  targets?: FabricObject[];
}

export interface DrawingObject {
  isDrawModeOn: boolean;
  isImageBeingGenerated: boolean;
  drawBrush: DrawObject;
}

export class PosterDrawing extends CommonMethods {
  public poster: Poster;
  public isDrawModeOn = false;
  public isImageBeingGenerated = false;
  public drawBrush: Draw;
  public lastDrawBrushColor: RGBA = DEFAULT_DRAW_COLOR;
  public drawnFabricObjectsHashMap: Record<string, FabricObject[]> = {};

  public constructor(poster: Poster) {
    super();
    this.poster = poster;
    this.drawBrush = new Draw();
  }

  public toObject(): DrawingObject {
    return {
      isDrawModeOn: this.isDrawModeOn,
      isImageBeingGenerated: this.isImageBeingGenerated,
      drawBrush: this.drawBrush.toObject(),
    };
  }

  private async setDrawMode(modeState: boolean): Promise<void> {
    this.isDrawModeOn = modeState;
    for (const [, eachPage] of Object.entries(this.poster.pages.pagesHashMap)) {
      const pageCanvas = eachPage.fabricCanvas;
      if (pageCanvas instanceof Canvas) {
        pageCanvas.isDrawingMode = this.isDrawModeOn;
      }
    }
    if (this.isDrawModeOn) {
      this.drawBrush.strokeColor = [...this.lastDrawBrushColor];
      this.poster.clearSelection();
      this.initDrawBrush(this.drawBrush);
      await this.poster.history.addDrawingHistory();

      trackPosterBuilderGA4Events(GA4EventName.START_DRAWING);
    } else {
      this.toggleGenerationState(true);
      this.toggleDrawingLoadingStates();
      await this.exitPencilDrawingMode();
      this.hideDrawingLoader();
      this.toggleGenerationState(false);
    }
  }

  toggleGenerationState = (state: boolean): void => {
    this.updateFromObject({
      isImageBeingGenerated: state,
    });
  };

  public isInEraseMode(): boolean {
    for (const [, eachPage] of Object.entries(this.poster.pages.pagesHashMap)) {
      const canvas = eachPage.fabricCanvas;
      if (!(canvas instanceof Canvas)) {
        return false;
      }

      if (canvas.isDrawingMode && canvas.freeDrawingBrush instanceof EraserBrush) {
        return true;
      }
    }

    return false;
  }

  public onPathDrawn(e: CanvasEvents['path:created'], pageID: string): void {
    this.addPath(e);
    e.path.shadow = null;
    e.path.set({
      erasable: true,
    });

    const objectsInPage = this.drawnFabricObjectsHashMap[pageID] ?? [];
    objectsInPage.push(e.path);
    this.drawnFabricObjectsHashMap[pageID] = objectsInPage;
    void this.poster.history.addDrawingHistory();
  }

  public async getDrawnFabricObjects(): Promise<Record<string, FabricObject[]>> {
    const result: Record<string, FabricObject[]> = {};
    for (const [pageId, fabricObjects] of Object.entries(this.drawnFabricObjectsHashMap)) {
      // eslint-disable-next-line no-await-in-loop
      result[pageId] = await this.cloneDrawingObjects(fabricObjects);
    }
    return result;
  }

  public removeFabricDrawingObjectFromAllPages(): void {
    for (const [pageId, fabricObjects] of Object.entries(this.drawnFabricObjectsHashMap)) {
      const page = this.poster.pages.pagesHashMap[pageId];
      const canvas = page.fabricCanvas;
      for (const obj of fabricObjects) {
        canvas.remove(obj);
      }
    }
  }

  public async updateCurrentDrawingFromFabricObjects(fabricObjectsByPage: Record<string, FabricObject[]>): Promise<void> {
    this.clearAllDrawnObjects();

    for (const [pageId, newFabricObjects] of Object.entries(fabricObjectsByPage)) {
      const page = this.poster.pages.pagesHashMap[pageId];
      if (!page) continue;

      const canvas = page.fabricCanvas;
      if (!(canvas instanceof Canvas)) {
        continue;
      }

      // eslint-disable-next-line no-await-in-loop
      const clonedObjects = await this.cloneDrawingObjectsForCanvas(newFabricObjects);
      canvas.add(...clonedObjects);
      this.drawnFabricObjectsHashMap[pageId] = clonedObjects;
    }
  }

  public addPath(e: DrawingEvent): void {
    if (e.path && !this.isInEraseMode()) {
      e.path.shadow = null;
      e.path.set({
        erasable: true,
      });

      e.path.set('selectable', false);
    }
  }

  public async onErasingEnd(e: ErasingEvent<'end'>): Promise<void> {
    e.preventDefault();
    for (const [, pageObject] of Object.entries(this.poster.pages.pagesHashMap)) {
      const canvas = pageObject.fabricCanvas;
      if (!(canvas instanceof Canvas) || !(canvas.freeDrawingBrush instanceof EraserBrush)) {
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      await canvas.freeDrawingBrush.commit(e.detail);
      // eslint-disable-next-line no-await-in-loop
      await this.poster.history.addDrawingHistory();
      canvas.requestRenderAll();
    }
  }

  public async uploadDrawingToCloud(drawingGroup: Group, pageId: string): Promise<any> {
    try {
      const tempUploadingImageUID = getUniqueString();
      const imageURL = this.uploadDrawing(drawingGroup, tempUploadingImageUID);
      await this.poster.pages.pagesHashMap[pageId].items.addItems.addImageItem(
        {
          left: drawingGroup.left,
          top: drawingGroup.top,
          scaledWidth: drawingGroup.width,
          scaledHeight: drawingGroup.height,
          scaleX: 1,
          scaleY: 1,
          extension: DRAWING_IMAGE_EXT,
          source: ImageItemSource.DRAWING,
          type: ElementDataType.IMAGE,
          hasTransparency: true,
          uploaderId: getUserId() ?? 0,
          dataUrl: imageURL,
          uid: tempUploadingImageUID,
        },
        {
          effects: {
            tintColor: [0, 0, 0, 1],
          },
        }
      );
    } catch (e) {
      getPosterUi().openErrorModal({
        message: window.i18next.t('pmwjs_drawing_upload_error'),
      });
      console.error(e);
    } finally {
      this.clearDrawnFabricObjects(pageId);
    }
  }

  public async newUploadFunction(groupHash: Record<string, Group> | undefined): Promise<void> {
    if (!groupHash) {
      return;
    }
    for (const [pageId, eachGroup] of Object.entries(groupHash)) {
      // eslint-disable-next-line no-await-in-loop
      const isDrawingVisible = eachGroup ? await isObjectVisible(eachGroup) : false;
      if (!isDrawingVisible || !eachGroup) {
        return;
      }

      // eslint-disable-next-line no-await-in-loop
      await this.uploadDrawingToCloud(eachGroup, pageId);
    }
  }

  public async exitPencilDrawingMode(): Promise<void> {
    for (const [, eachPage] of Object.entries(this.poster.pages.pagesHashMap)) {
      const pageCanvas = eachPage.fabricCanvas;
      if (pageCanvas instanceof Canvas) {
        pageCanvas.isDrawingMode = false;
      }
    }
    this.poster.history.removeDrawingHistoryStates();
    const drawingGroupHashmap = this.getGroupHashmapForDrawnObjects();
    await this.newUploadFunction(drawingGroupHashmap);
  }

  public clearDrawnFabricObjects(pageID: string): void {
    const canvas = this.poster.pages.pagesHashMap[pageID].fabricCanvas;
    for (const drawnFabricObject of this.drawnFabricObjectsHashMap[pageID]) {
      canvas.remove(drawnFabricObject);
    }
    delete this.drawnFabricObjectsHashMap[pageID];
  }

  public uploadDrawing(drawingGroup: Group, tempUploadingImageUID?: string): string {
    const storageDrawingImage = drawingGroup.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: getScaleToFit(HIGH_RES_DRAWING_MAX_DIMENSION, HIGH_RES_DRAWING_MAX_DIMENSION, drawingGroup.getScaledWidth(), drawingGroup.getScaledHeight()),
    });
    const source = ImageItemSource.DRAWING;
    const UPLOAD_LOADING_KEY = 'uploadingUserMedia';

    const fd = new FormData();
    fd.append('Filedata', storageDrawingImage);
    fd.append('type', UserMediaType.DRAWING);
    if (tempUploadingImageUID) {
      fd.append('tempUploadingImageUID', tempUploadingImageUID);
    }

    void uploadFile(fd, UPLOAD_LOADING_KEY, UserMediaType.DRAWING, source);
    return storageDrawingImage;
  }

  public getGroupHashmapForDrawnObjects(): Record<string, Group> | undefined {
    const drawnObjectsGroupHashmap: Record<string, Group> = {};
    if (!Object.entries(this.drawnFabricObjectsHashMap).length) {
      return undefined;
    }
    for (const [pageID, drawnObjects] of Object.entries(this.drawnFabricObjectsHashMap)) {
      drawnObjectsGroupHashmap[pageID] = new Group(drawnObjects);
    }
    return drawnObjectsGroupHashmap;
  }

  public toggleDrawingLoadingStates(): void {
    const isDrawingLoaderActive = isLoaderActiveForKey(DRAWING_LOADING_KEY);
    if (isDrawingLoaderActive) {
      this.hideDrawingLoader();
    } else {
      this.showDrawingLoader();
    }
  }

  public hideDrawingLoader(): void {
    hideLoading(DRAWING_LOADING_KEY);
  }

  public showDrawingLoader(): void {
    showLoading(DRAWING_LOADING_KEY, {
      text: window.i18next.t('pmwjs_finishing_drawing'),
      hideIcon: true,
    });
  }

  public initDrawBrush(drawVO: Draw): void {
    this.changeLineBrush(drawVO.brush);
    this.changeLineColor(rgbaToRgbaString(drawVO.strokeColor));
    this.changeLineWidth(drawVO.strokeWeight);
  }

  public changeLineBrush(brushType: string): void {
    for (const [pageId, eachPage] of Object.entries(this.poster.pages.pagesHashMap)) {
      const pageCanvas = eachPage.fabricCanvas;
      if (pageCanvas instanceof Canvas) {
        if (brushType === PENCIL_BRUSH) {
          pageCanvas.freeDrawingBrush = new PencilBrush(pageCanvas);
        } else if (brushType === CIRCLE_BRUSH) {
          pageCanvas.freeDrawingBrush = new CircleBrush(pageCanvas);
        } else if (brushType === SPRAY_BRUSH) {
          pageCanvas.freeDrawingBrush = new SprayBrush(pageCanvas);
        } else if (brushType === ERASER_BRUSH) {
          const eraserBrush = new EraserBrush(pageCanvas);
          eraserBrush.on('end', this.onErasingEnd.bind(this));
          pageCanvas.freeDrawingBrush = eraserBrush;
        } else {
          throw new Error('unsupported brush selected');
        }
        this.changeLineBrushCursor(brushType, pageId);
      }
    }
  }

  public changeLineBrushCursor(brushType: string, pageID: string): void {
    const brushCursorPlacement = '10 25 , auto';
    const brushTypes = [PENCIL_BRUSH, CIRCLE_BRUSH, SPRAY_BRUSH, ERASER_BRUSH];
    if (!brushTypes.includes(brushType)) {
      throw new Error('Invalid brush type passed');
    }
    const brushCursorSVGPath = `url("${window.PMW.util.asset_url(`images/postermaker/drawing-mode-cursors/${brushType}.svg`)}")`;
    const pageCanvas = this.poster.pages.pagesHashMap[pageID].fabricCanvas;
    if (pageCanvas instanceof Canvas) {
      pageCanvas.freeDrawingCursor = `${brushCursorSVGPath} ${brushCursorPlacement}`;
    }
  }

  public changeLineColor(color: string): void {
    for (const [, eachPage] of Object.entries(this.poster.pages.pagesHashMap)) {
      const pageCanvas = eachPage.fabricCanvas;
      if (pageCanvas instanceof Canvas) {
        const {freeDrawingBrush} = pageCanvas;
        if (freeDrawingBrush) {
          freeDrawingBrush.color = color;
        }
      }
    }
  }

  public changeLineWidth(width: number): void {
    for (const [, eachPage] of Object.entries(this.poster.pages.pagesHashMap)) {
      const pageCanvas = eachPage.fabricCanvas;
      if (pageCanvas instanceof Canvas) {
        const {freeDrawingBrush} = pageCanvas;
        if (freeDrawingBrush) {
          freeDrawingBrush.width = parseInt(width.toString(), 10);
        }
      }
    }
  }

  public onDrawBrushSelected(brushType: string): void {
    if (this.drawBrush) {
      this.drawBrush.brush = brushType;
      this.changeLineBrush(this.drawBrush.brush);

      if (brushType === ERASER_BRUSH) {
        this.changeLineWidth(this.drawBrush.strokeWeight);
      } else {
        this.changeLineWidth(this.drawBrush.strokeWeight);
        this.changeLineColor(rgbaToRgbaString(this.drawBrush.strokeColor));
      }
    }
  }

  public invalidate(drawObject: DeepPartial<DrawingObject>): void {
    if ('isDrawModeOn' in drawObject) {
      void this.setDrawMode(this.isDrawModeOn);
      this.onDrawBrushSelected(PENCIL_BRUSH);
    }
    if (this.drawBrush.brush) {
      this.onDrawBrushSelected(this.drawBrush.brush);
    }
    if (this.drawBrush.strokeWeight) {
      this.changeLineWidth(this.drawBrush.strokeWeight);
    }
    if (this.drawBrush.strokeColor) {
      this.changeLineColor(rgbaToRgbaString(this.drawBrush.strokeColor));
    }
  }

  public override copyVals(obj: DeepPartial<DrawingObject> = {}): void {
    const {drawBrush, ...itemObj} = obj;
    super.copyVals(itemObj);
    this.drawBrush.copyVals(drawBrush);
  }

  public updateFromObject(drawObject: DeepPartial<DrawingObject>): void {
    this.copyVals(drawObject);
    this.invalidate(drawObject);
    this.poster.redux.updateReduxData();
  }

  public toggleDrawMode(): void {
    this.updateFromObject({
      isDrawModeOn: !this.isDrawModeOn,
    });
  }

  public getDefaultStrokeWeightForBrush(id: string): number {
    switch (id) {
      case ERASER_BRUSH:
        return 25;
      case SPRAY_BRUSH:
        return 20;
      case PENCIL_BRUSH:
      case CIRCLE_BRUSH:
      default:
        return 10;
    }
  }

  public updateBrushType(brushName: string): void {
    this.updateFromObject({
      drawBrush: {
        brush: brushName,
        strokeWeight: this.getDefaultStrokeWeightForBrush(brushName),
      },
    });
  }

  public updateBrushSize(size: number): void {
    this.updateFromObject({
      drawBrush: {
        strokeWeight: size,
      },
    });
  }

  public updateDrawModeColor(color: RGBA): void {
    this.updateFromObject({
      drawBrush: {
        strokeColor: color,
      },
    });
  }

  public disableDrawMode(): void {
    this.updateFromObject({
      isDrawModeOn: false,
    });
  }

  private async cloneDrawingObjects(fabricObjects: FabricObject[]): Promise<FabricObject[]> {
    const clones = await Promise.all(fabricObjects.map((obj) => obj.clone()));
    for (const obj of clones) {
      obj.set({erasable: true});
    }
    return clones;
  }

  private async cloneDrawingObjectsForCanvas(fabricObjects: FabricObject[]): Promise<FabricObject[]> {
    const clones = await this.cloneDrawingObjects(fabricObjects);
    for (const obj of clones) {
      obj.set({selectable: false});
    }
    return clones;
  }

  private clearAllDrawnObjects(): void {
    this.removeFabricDrawingObjectFromAllPages();
    this.drawnFabricObjectsHashMap = {};
  }
}
